/**
 * HUG - Artist Management Platform
 * Copyright (c) 2025 Underplay Music Ltd. All rights reserved.
 * 
 * This software and associated documentation files are the proprietary 
 * property of Underplay Music Ltd and are protected by copyright law.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, TrendingUp, TrendingDown, DollarSign, Receipt, Calendar, Edit, Trash2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Finance = ({ user, selectedArtist }) => {
  const [financeSummary, setFinanceSummary] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [editExpenseDialogOpen, setEditExpenseDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    amount: '',
    description: '',
    category: 'other',
    date: new Date().toISOString().split('T')[0],
    live_show_id: '',
  });
  const [receiptFiles, setReceiptFiles] = useState([]);
  const [shows, setShows] = useState([]);

  const artistId = user?.role === 'artist' ? user.id : selectedArtist;
  const isManagementView = selectedArtist === 'management';

  useEffect(() => {
    if (artistId || isManagementView) {
      fetchFinanceSummary();
      fetchShows();
    }
  }, [artistId, isManagementView]);

  const fetchShows = async () => {
    try {
      const token = localStorage.getItem('token');
      // Don't fetch shows for management view
      if (isManagementView) {
        setShows([]);
        return;
      }
      const params = artistId && artistId !== 'all' ? `?artist_id=${artistId}` : '';
      const response = await axios.get(`${API}/live-shows${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShows(response.data);
    } catch (error) {
      
      // Silent fail - shows list is supplementary
    }
  };

  const fetchFinanceSummary = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = {};
      if (isManagementView) {
        params.filter_type = 'management';
      } else if (artistId && artistId !== 'all') {
        params.artist_id = artistId;
      }
      const response = await axios.get(`${API}/finance/summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setFinanceSummary(response.data);
    } catch (error) {
      toast.error('Failed to fetch finance summary');
    } finally {
      setLoading(false);
    }
  };

  const handleReceiptUpload = (e) => {
    const files = Array.from(e.target.files);
    const promises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          resolve({
            filename: file.name,
            file_data: event.target.result.split(',')[1],
            file_type: file.type,
          });
        };
        reader.readAsDataURL(file);
      });
    });
    
    Promise.all(promises).then(uploadedFiles => {
      setReceiptFiles(prev => [...prev, ...uploadedFiles]);
      toast.success(`${files.length} receipt(s) uploaded`);
    });
  };

  const removeReceipt = (index) => {
    setReceiptFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/expenses`,
        {
          artist_id: artistId,
          amount: parseFloat(expenseForm.amount),
          description: expenseForm.description,
          category: expenseForm.category,
          date: new Date(expenseForm.date).toISOString(),
          live_show_id: expenseForm.live_show_id || null,
          receipts: receiptFiles,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Expense added successfully!');
      setExpenseDialogOpen(false);
      setExpenseForm({
        amount: '',
        description: '',
        category: 'other',
        date: new Date().toISOString().split('T')[0],
        live_show_id: '',
      });
      setReceiptFiles([]);
      await fetchFinanceSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add expense');
    }
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      amount: expense.amount.toString(),
      description: expense.description,
      category: expense.category,
      date: new Date(expense.date).toISOString().split('T')[0],
    });
    setEditExpenseDialogOpen(true);
  };

  const handleUpdateExpense = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/expenses/${editingExpense.id}`,
        {
          artist_id: editingExpense.artist_id,
          amount: parseFloat(expenseForm.amount),
          description: expenseForm.description,
          category: expenseForm.category,
          date: new Date(expenseForm.date).toISOString(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Expense updated successfully!');
      setEditExpenseDialogOpen(false);
      setEditingExpense(null);
      fetchFinanceSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update expense');
    }
  };

  const confirmDeleteExpense = (expense) => {
    setExpenseToDelete(expense);
    setDeleteDialogOpen(true);
  };

  const handleDeleteExpense = async () => {
    if (!expenseToDelete) return;

    try {
      const token = localStorage.getItem('token');
      
      await axios.delete(`${API}/expenses/${expenseToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success('Expense deleted successfully');
      setDeleteDialogOpen(false);
      setExpenseToDelete(null);
      
      await fetchFinanceSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete expense');
    }
  };

  const handleTakeCommission = async () => {
    if (!window.confirm(`Are you sure you want to take commission of £${financeSummary?.commission?.owed?.toFixed(2)}? This will generate an invoice.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/finance/take-commission`,
        { artist_id: artistId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      fetchFinanceSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to take commission');
    }
  };

  const formatCurrency = (amount) => {
    return `£${amount.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (!artistId || artistId === 'all') {
    return (
      <div className="space-y-8">
        <Card className="bg-card border-border/50 p-12 text-center">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Please select an artist to view their finances.</p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-8">
        <Card className="bg-card border-border/50 p-12 text-center">
          <p className="text-muted-foreground">Loading financial data...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="finance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-bold text-2xl text-foreground">
            {user?.role === 'artist' ? 'My Expenses' : 'Expenses'}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {user?.role === 'artist' 
              ? 'View your income, expenses, and financial performance'
              : 'Complete financial overview and expense tracking'
            }
          </p>
        </div>
        <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" />
              Add Expense
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border/50">
            <DialogHeader>
              <DialogTitle className="font-manrope font-bold text-2xl">Add New Expense</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddExpense} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount (£)</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                  required
                  className="bg-input/50 border-border rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  placeholder="What was this expense for?"
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                  required
                  className="bg-input/50 border-border rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select value={expenseForm.category} onValueChange={(value) => setExpenseForm({ ...expenseForm, category: value })}>
                  <SelectTrigger className="bg-input/50 border-border rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="travel">Travel</SelectItem>
                    <SelectItem value="accommodation">Accommodation</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={expenseForm.date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                  required
                  className="bg-input/50 border-border rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="live-show">Link to Live Show (Optional)</Label>
                <Select 
                  value={expenseForm.live_show_id || "none"} 
                  onValueChange={(value) => setExpenseForm({ ...expenseForm, live_show_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="bg-input/50 border-border rounded-xl">
                    <SelectValue placeholder="Select a show (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None - General Expense</SelectItem>
                    {shows.map(show => (
                      <SelectItem key={show.id} value={show.id}>
                        {show.venue} - {new Date(show.date).toLocaleDateString()} ({show.city})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Optional: Link this expense to a specific live show
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="receipts">Upload Receipts (Optional)</Label>
                <Input
                  id="receipts"
                  type="file"
                  accept="image/*,.pdf"
                  multiple
                  onChange={handleReceiptUpload}
                  className="bg-input/50 border-border rounded-xl"
                />
                <p className="text-xs text-muted-foreground">Upload receipt images or PDFs</p>
                {receiptFiles.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {receiptFiles.map((file, index) => (
                      <div key={file.filename || `file-${index}`} className="flex items-center justify-between p-2 bg-accent/20 rounded-lg">
                        <span className="text-sm truncate flex-1">{file.filename}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeReceipt(index)}
                          className="h-6 w-6 p-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button type="submit" className="w-full bg-primary hover:bg-primary/90">
                Add Expense
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Expense Dialog */}
      <Dialog open={editExpenseDialogOpen} onOpenChange={setEditExpenseDialogOpen}>
        <DialogContent className="bg-card border-border/50">
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold text-2xl">Edit Expense</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateExpense} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-amount">Amount (£)</Label>
              <Input
                id="edit-amount"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={expenseForm.amount}
                onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                required
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Input
                id="edit-description"
                placeholder="What was this expense for?"
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                required
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-category">Category</Label>
              <Select value={expenseForm.category} onValueChange={(value) => setExpenseForm({ ...expenseForm, category: value })}>
                <SelectTrigger className="bg-input/50 border-border rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="travel">Travel</SelectItem>
                  <SelectItem value="accommodation">Accommodation</SelectItem>
                  <SelectItem value="equipment">Equipment</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-date">Date</Label>
              <Input
                id="edit-date"
                type="date"
                value={expenseForm.date}
                onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                required
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90">
              Update Expense
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="bg-card border-border/50 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Income</p>
              <p className="font-manrope font-bold text-xl text-foreground">{formatCurrency(financeSummary?.income?.total || 0)}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-card border-border/50 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Costs</p>
              <p className="font-manrope font-bold text-xl text-foreground">{formatCurrency(financeSummary?.costs?.total || 0)}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-card border-border/50 p-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              (financeSummary?.net_profit || 0) >= 0 ? 'bg-blue-500/20' : 'bg-orange-500/20'
            }`}>
              <DollarSign className={`w-5 h-5 ${
                (financeSummary?.net_profit || 0) >= 0 ? 'text-blue-500' : 'text-orange-500'
              }`} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Net Profit</p>
              <p className={`font-manrope font-bold text-xl ${
                (financeSummary?.net_profit || 0) >= 0 ? 'text-foreground' : 'text-orange-500'
              }`}>
                {formatCurrency(financeSummary?.net_profit || 0)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="bg-card border-border/50 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <Receipt className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Commission Owed</p>
              <p className="font-manrope font-bold text-xl text-foreground">{formatCurrency(financeSummary?.commission?.owed || 0)}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Commission Action - Manager Only */}
      {user?.role === 'manager' || user?.role === 'admin' && selectedArtist && selectedArtist !== 'all' && (financeSummary?.commission?.owed || 0) > 0 && (
        <Card className="bg-card border-border/50 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-base">Management Commission Available</h3>
              <p className="text-sm text-muted-foreground mt-1">
                20% of net profit (£{financeSummary?.net_profit?.toFixed(2)}) = £{financeSummary?.commission?.owed?.toFixed(2)}
              </p>
            </div>
            <Button onClick={handleTakeCommission} className="bg-primary hover:bg-primary/90">
              Take Commission
            </Button>
          </div>
        </Card>
      )}
      
      {/* Artist View - Show Commission Info (Read-Only) */}
      {user?.role === 'artist' && (financeSummary?.commission?.owed || 0) > 0 && (
        <Card className="bg-card border-border/50 p-4">
          <div>
            <h3 className="font-semibold text-base">Management Commission</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Your net profit: £{financeSummary?.net_profit?.toFixed(2)}
            </p>
            <p className="text-sm text-muted-foreground">
              Commission owed to management (20%): £{financeSummary?.commission?.owed?.toFixed(2)}
            </p>
          </div>
        </Card>
      )}

      {/* Income Breakdown */}
      <Card className="bg-card border-border/50 p-4">
        <h3 className="font-semibold text-lg mb-4">Income Breakdown</h3>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Live Shows</span>
            <span className="font-semibold">{formatCurrency(financeSummary?.income?.live_shows || 0)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Paid Invoices</span>
            <span className="font-semibold">{formatCurrency(financeSummary?.income?.invoices || 0)}</span>
          </div>
          <div className="border-t border-border/30 pt-2 mt-2">
            <div className="flex justify-between items-center font-bold">
              <span>Total Income</span>
              <span className="text-green-500">{formatCurrency(financeSummary?.income?.total || 0)}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Costs Breakdown */}
      <Card className="bg-card border-border/50 p-4">
        <h3 className="font-semibold text-lg mb-4">Costs Breakdown</h3>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Live Show Costs</span>
            <span className="font-semibold">{formatCurrency(financeSummary?.costs?.live_show_costs || 0)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">General Expenses</span>
            <span className="font-semibold">{formatCurrency(financeSummary?.costs?.expenses || 0)}</span>
          </div>
          <div className="border-t border-border/30 pt-2 mt-2">
            <div className="flex justify-between items-center font-bold">
              <span>Total Costs</span>
              <span className="text-red-500">{formatCurrency(financeSummary?.costs?.total || 0)}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Expenses List */}
      {financeSummary?.breakdown?.expenses && financeSummary.breakdown.expenses.length > 0 && (
        <div>
          <h3 className="font-semibold text-lg mb-3">Expenses</h3>
          <div className="grid gap-3">
            {financeSummary.breakdown.expenses.map((expense) => (
              <Card key={expense.id} className="bg-card border-border/50 p-4">
                <div className="flex flex-col">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-base">{expense.description}</h4>
                        <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded capitalize">
                          {expense.category}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(expense.date)}
                        </div>
                        <div className="font-semibold text-foreground">
                          {formatCurrency(expense.amount)}
                        </div>
                        {expense.linked_show_id && (
                          <span className="text-xs bg-blue-500/20 text-blue-500 px-2 py-0.5 rounded">
                            Linked to Show
                          </span>
                        )}
                        {expense.receipt_file && (
                          <span className="text-xs bg-green-500/20 text-green-500 px-2 py-0.5 rounded">
                            Has Receipt
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditExpense(expense)}
                      className="h-8 px-3 text-xs"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                  </div>
                  <div className="flex justify-end pt-3 border-t border-border/30">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => confirmDeleteExpense(expense)}
                      className="min-w-[90px] h-8 text-xs"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Commission History - Manager Only (when viewing artist) */}
      {user?.role === 'manager' || user?.role === 'admin' && selectedArtist && selectedArtist !== 'all' && financeSummary?.commission?.records && financeSummary.commission.records.length > 0 && (
        <div>
          <h3 className="font-semibold text-lg mb-3">Commission History</h3>
          <Card className="bg-card border-border/50 p-4">
            <div className="space-y-2">
              {financeSummary.commission.records.map((record) => (
                <div key={record.id} className="flex justify-between items-center py-2 border-b border-border/30 last:border-0">
                  <div>
                    <p className="text-sm font-semibold">{formatDate(record.created_at)}</p>
                    <p className="text-xs text-muted-foreground">
                      20% of £{record.calculation_base?.toFixed(2)}
                    </p>
                  </div>
                  <span className="font-semibold text-primary">{formatCurrency(record.amount)}</span>
                </div>
              ))}
              <div className="pt-2 flex justify-between items-center font-bold">
                <span>Total Commission Taken</span>
                <span className="text-primary">{formatCurrency(financeSummary?.commission?.total_taken || 0)}</span>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setExpenseToDelete(null);
        }}
        onConfirm={handleDeleteExpense}
        title="Delete Expense"
        itemName={expenseToDelete?.description}
        description="Are you sure you want to delete this expense? This action cannot be undone."
      />
    </div>
  );
};

export default Finance;
